import { unwrapResult } from '@reduxjs/toolkit';
import type {
  RcFile, UploadChangeParam, UploadFile,
} from 'antd/lib/upload/interface';
import React, {
  CSSProperties,
  FC, useEffect, useState,
} from 'react';
import styled from 'styled-components';

import { defaultErrorMessage } from '~/api/provider/providerErrors';
import { Upload } from '~/atomic/atom/Upload';
import { getAuthTokenFromClient } from '~/feature/authorization/getAuthToken';
import { environments } from '~/lib/const';

import { CloseIcon } from '../atom/icon/CloseIcon';
import { LoaderIcon } from '../atom/icon/LoaderIcon';
import { TextSmallStyle } from '../Typography';

export interface Size {
  width: number;
  height: number;
}

interface FileUploadProps {
  previewText: string;
  url?: string;
  action?: string;
  name?: string;
  afterUpload?(url?: string): any;
  beforeUpload?(file: RcFile, Size): boolean;
  onRemove?(): Promise<any>;
  className?: string;
  style?: CSSProperties;
}

function getBase64(img: File | Blob, callback: (url:string | ArrayBuffer) => any) {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result));
  reader.readAsDataURL(img);
}

export const FileUpload: FC<FileUploadProps> = ({
  action, name, afterUpload, previewText, onRemove, url, beforeUpload, ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(url);
  const [isValid, setIsValid] = useState(false);
  const [headers, changeHeaders] = useState({
    Accept: 'application/json',
    'X-Requested-With': null,
  });

  useEffect(() => {
    if (environments.isClient) {
      const token = getAuthTokenFromClient();

      if (token) {
        changeHeaders((oldHeaders) => ({
          ...oldHeaders,
          authorization: `Bearer ${token}`,
        }));
      }
    }
  }, []);

  useEffect(() => {
    if (url) {
      setImageUrl(url);
    }
  }, [url]);

  const setError = async (error: string) => {
    const { message } = await import('~/atomic/atom/message');

    if (error.length) {
      message.error(error);
    }
  };

  const onChangeHandler = (info: UploadChangeParam<UploadFile<any>>) => {
    if (imageUrl) {
      return;
    }

    if (!isValid) {
      return;
    }

    if (info.file?.status === 'uploading') {
      setLoading(true);
      return;
    }
    setLoading(false);
    if (info.file?.status === 'done') {
      getBase64(
        info.file.originFileObj,
        (newUrl) => {
          if (afterUpload && typeof newUrl !== 'string') afterUpload();
          if (typeof newUrl === 'string') {
            if (afterUpload) afterUpload(newUrl);
            setImageUrl(newUrl);
          }
        },
      );
    }

    if (info.file?.status === 'error') {
      const { response } = info.file;

      if (info.file.response?.errors?.image?.length) {
        setError(info.file.response?.errors?.image[0] ?? defaultErrorMessage);
      } else {
        setError(
          response?.message === '' ? defaultErrorMessage : response?.message,
        );
      }
    }
  };

  const onRemoveHandler = () => {
    setLoading(true);
    onRemove()
      .then(unwrapResult)
      .then(() => {
        setLoading(false);
        setImageUrl(undefined);
      });
  };

  const beforeUploadHandler = async (file: RcFile): Promise<any> => {
    const fileSize = await (new Promise<{ width: number;height: number; }>((resolve, reject) => {
      if (file) {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = function () {
          resolve({ width: img.width, height: img.height });

          URL.revokeObjectURL(objectUrl);
        };

        img.src = objectUrl;
      } else {
        reject(false);
      }
    }));

    const isValidBeforeUpload = await beforeUpload(file, fileSize);

    setIsValid(isValidBeforeUpload);
    return isValidBeforeUpload;
  };

  const customRequest = isValid
    ? undefined
    : (options) => {
      options.onError(new Error(''));
    };

  return (
    <SCUpload>
      <Upload
        beforeUpload={beforeUploadHandler}
        action={action}
        listType="picture-card"
        withCredentials
        showUploadList={false}
        onChange={onChangeHandler}
        disabled={!!imageUrl || loading}
        headers={headers}
        name={name}
        customRequest={customRequest}
        {...props}
      >
        <SCWrapper>
          {loading && (
            <SCLoaderWrap>
              <SCLoaderIcon />
            </SCLoaderWrap>
          )}
          {!imageUrl ? (
            <SCText>
              {previewText}
            </SCText>
          ) : (
            <>
              <SCPreviewImage src={imageUrl} alt="" />
              {!loading && (
                <SCCloseIconWrap
                  onClick={onRemoveHandler}
                >
                  <SCCloseIcon />
                </SCCloseIconWrap>
              )}
            </>
          )}
        </SCWrapper>
      </Upload>
    </SCUpload>
  );
};

const SCText = styled.span`
  ${TextSmallStyle};
  color: var(--primary-color);
`;

const SCCloseIconWrap = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  box-shadow: inset -10px 0 100px 0 rgba(50, 50, 50, 0.75);
  transition: 0.3s;
  transition-property: opacity;
  opacity: 0;
  pointer-events: none;
`;

const SCCloseIcon = styled(CloseIcon)`
    position: absolute;
    top: 50%;
    right: 50%;
    transform: translateX(50%) translateY(-50%);
    font-size: 11px;
    color: var(--white-color); 
`;

const SCUpload = styled.div`
  .ant-upload.ant-upload-select > .ant-upload {
    padding: 0;
  }
  
  .ant-upload.ant-upload-disabled {
    cursor: pointer;
  }

  .ant-upload.ant-upload-select {
    &:hover {
      ${SCCloseIconWrap} {
        opacity: 0.7;
        pointer-events: auto;
      }
    }
  }
`;

const SCPreviewImage = styled.img`
  width: 100%;
`;

const SCWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 100%;
`;

const SCLoaderWrap = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: var(--bg-color);
  z-index: 2;
`;

const SCLoaderIcon = styled(LoaderIcon)`
  width: 40px;
  height: 40px;
  position: absolute;
  top: 50%;
  right: 50%;
  transform: translateX(50%) translateY(-50%);
`;
